import './popup.scss'
import { signInModalStore } from '@common/stores/signInModalStore'
import { isPast } from '@common/utils/date/isPast'
import { setLocalStorage } from '@common/utils/localStorage/setLocalStorage'
import { getSessionStorage } from '@common/utils/session/getSessionStorage'
import { setSessionStorage } from '@common/utils/session/setSessionStorage'
import { useLogger } from '@omnia/ui/src/lib/hooks/useLogger'
import { Modal } from '@vista-digital/occ-ui'
import { useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom-v7'
import { cmsDataService } from '@/content/services/cms.data.service'
import { cmsService } from '@/content/services/cms.service'
import { extensions } from '@/content/services/extensions'
import type GlobalPopup from './models'
import getPopupProps from './utils/getPopupProps'
import getPopupStorageData from './utils/getPopupStorageData'
import { getSinglePopupId } from './utils/getSinglePopupId'
import getUpdatedStorageData from './utils/getUpdatedStorageData'

const Popup = () => {
    const [enabledPopup, setEnabledPopup] = useState<GlobalPopup | undefined>()
    const [showPopup, setShowPopup] = useState<boolean>(false)

    const location = useLocation()
    const logger = useLogger('global-popup')
    const initialPopupData = cmsDataService.get()?.popups

    logger.debug('Popup rerendered')

    useEffect(() => {
        void (async () => {
            const singlePopupId = getSinglePopupId(location.pathname, initialPopupData)
            if (!singlePopupId) return

            try {
                const { singlePopupStorageData, popupStorageData } = getPopupStorageData(singlePopupId)
                if (singlePopupStorageData && !isPast(singlePopupStorageData.dateCanShow)) return

                const popupSessionId = getSessionStorage<number>(`popup-${singlePopupId}`)
                if (popupSessionId === singlePopupStorageData?.id && singlePopupStorageData?.displayFrequency === 0) {
                    return
                }

                const singlePopup = (
                    await cmsService.getExtensions(extensions.GlobalPopup, singlePopupId.toString())
                )[0]

                const updatedPopupStorageData = getUpdatedStorageData(
                    popupStorageData ?? undefined,
                    singlePopup,
                    singlePopupId
                )

                setEnabledPopup(singlePopup)
                setShowPopup(true)
                setLocalStorage('popup-data', updatedPopupStorageData)
                setSessionStorage(`popup-${singlePopupId}`, singlePopupId)
            } catch (error: unknown) {
                logger.error(`Error retrieving GlobalPopup id ${singlePopupId}.`, { error })
            }
        })()
        // eslint-disable-next-line react-hooks/exhaustive-deps -- Dependencies don't change
    }, [location])

    const closePopup = () => setShowPopup(false)
    const isShowing = !!enabledPopup && showPopup && !signInModalStore.isModalOpen

    const popupProps = getPopupProps(enabledPopup, closePopup, isShowing)

    return <>{isShowing && popupProps && <Modal {...popupProps} />}</>
}

export default Popup
